// FILE: assets/bold-quantity-picker.js
//
// CHANGES IN THIS VERSION
// ------------------------------------------------------------------
// Added "multiplier mode": an alternate slider behavior used by
// customize (Greek letter) products, where the picker's slider no
// longer represents raw order quantity, but a small integer "sets"
// value. The final order quantity is computed externally as
//   quantity = sets * getMultiplicand()
// where getMultiplicand() is supplied by the caller (see
// bold-customize-letters-ui.js) and typically returns the current
// total character count across one or more letter-entry inputs.
//
// Public API added:
//   picker.enableMultiplierMode({ min, max, step, initialValue, getMultiplicand })
//   picker.disableMultiplierMode()
//   picker.recomputeMultiplier()   // call whenever the multiplicand changes
//   picker.setSets(n)              // public setter, clamps to [min, max]
//
// While multiplier mode is active:
//   - The slider's min/max/step come from config, not from SLIDER_ZONES.
//   - The native quantity-input's -/+ buttons are intercepted (capture
//     phase, on the wrapper) and repurposed to nudge "sets" by `step`
//     instead of incrementing/decrementing raw quantity.
//   - The numeric quantity input becomes read-only (`readOnly = true`,
//     class `is-computed`) so the only way to change quantity is via
//     sets (slider or steppers). It still holds the real, final
//     quantity value and still gets submitted with the form.
//   - A small hint element ("N sets of letters") is rendered near the
//     numeric input, mirroring the old bespoke customize-product UI.
//
// Everything still funnels through the existing debounce + `change`
// dispatch on numericInput, so the facade's existing
// `handleInteraction` -> `api.actions.updateQuantity` path is
// untouched and remains the single source of truth for `_store.quantity`.
// ------------------------------------------------------------------

if (!customElements.get("bold-quantity-picker")) {
    customElements.define(
        "bold-quantity-picker",
        class BoldQuantityPicker extends HTMLElement {
            constructor() {
                super();
                this.numericInput = null;
                this.rangeInput = null;
                this.wrapper = null;
                this.facade = null;
                this.isInitialized = false;
                this.isUnlocked = false;
                this.originalMatrixInput = null;

                this.SLIDER_ZONES = [
                    { max: 600, step: 1 },    
                    { max: 1200, step: 5 }
                ];

                this.calculatedZones = [];
                this.sliderMax = 0;

                // --- Multiplier mode state -------------------------------------
                this.multiplierMode = null; // { min, max, step, getMultiplicand } | null
                this.sets = 1;
                this._setsHintEl = null;
                this._setsHiddenEl = null;
                this._stepperCaptureBound = false;
                this._multiplierDebounce = null;

                // --- Count mode state ---------------------------------------
                this.countMode = null; // { getCount, formatHint } | null
                this._countHintEl = null;
                this._countDebounce = null;
                this._lastCountApplied = null;
                this._savedQuantity = null;
                this._savedMin = null;

                this._handleSliderChange = this._handleSliderChange.bind(this);
                this._handleMatrixInput = this._handleMatrixInput.bind(this);
                this._handleNumericInput = this._handleNumericInput.bind(this);
                this._checkUnlock = this._checkUnlock.bind(this);
                this._handleMultiplierSliderChange = this._handleMultiplierSliderChange.bind(this);
                this._handleStepperClickCapture = this._handleStepperClickCapture.bind(this);
                //this._moveTable = this._moveTable.bind(this);

                this.EVENTS = {
                    STATE_CHANGE_GENERAL: 'hub:state:change', 
                };
            }

            connectedCallback() {
                if (this.isInitialized) {
                    if (!this.isUnlocked) {
                        document.addEventListener("change", this._checkUnlock);
                        document.addEventListener("input", this._checkUnlock);
                        document.addEventListener(
                            this.EVENTS.STATE_CHANGE_GENERAL,
                            this._checkUnlock
                        );

                        this._checkUnlock();
                    }

                    return;
                }

                const adapter = document.querySelector("bold-sc-adapter");
                this.facade = adapter ? adapter.facade : null;

                if (this.facade && adapter.getAttribute("options") === "ready") {
                    this.init();
                } else {
                    document.addEventListener("hub:options:ready", () => {
                        const tryInit = (attempt = 0) => {
                            const a = document.querySelector("bold-sc-adapter");
                            this.facade = a ? a.facade : null;
                            if (this.facade || attempt >= 5) {
                                this.init();
                            } else {
                                setTimeout(() => tryInit(attempt + 1), 100);
                            }
                        };
                        tryInit();
                    }, { once: true });
                }

                //document.addEventListener(this.EVENTS.QUANTITY_BREAKS_LOADED, this._moveTable);
            }

            disconnectedCallback() {
                // Clean up listeners
                document.removeEventListener("change", this._checkUnlock);
                document.removeEventListener("input", this._checkUnlock);
                document.removeEventListener(
                    this.EVENTS.STATE_CHANGE_GENERAL,
                    this._checkUnlock
                );
                this._unbindStepperCapture();
            }

            init() {
                if (this.isInitialized) return;
                this.isInitialized = true;
                this.classList.remove("bold-initial-hide");
                this.numericInput = document.querySelector('input[name="quantity"], .quantity__input');
                if (!this.numericInput) return;
                this.wrapper =
                    this.numericInput.closest(".product-form__quantity, .quantity") || this.numericInput.parentElement;

                // TODO: Move initialization to price display
                const receipt = document.querySelector("bold-price-display");
                if (receipt) {
                    receipt.classList.remove("bold-mode-matrix");
                    receipt.classList.remove("bold-mode-integrated");
                }
                const matrixTarget = this._getMatrixTarget();
                if (matrixTarget) {
                    const safeName = CSS.escape(`properties[${matrixTarget.prettyName}]`);
                    this.originalMatrixInput = document.querySelector(`[name="${safeName}"]`);
                    this._swapPosition(matrixTarget.name);
                    this._renderMatrixMode(matrixTarget);
                    //this._moveTable();
                } else {
                    this._renderSliderMode();
                    //this._moveTable();
                }

                this._initProgressiveDisclosure();

                setTimeout(() => {
                    if (!matrixTarget) this._mergeReceipt();
                    this.facade.tools.dispatch('QUANTITY_PICKER_LOADED', { isMatrix: !!matrixTarget });
                    this._checkUnlock();
                }, 100);
            }

            // 5. NEW: Separate method to update quantity display only (fired by QTY_TOTAL_UPDATED)
            _updateQtyDisplay() {
                if (!this.facade) return;
                const total = this.facade.state.getQuantity();

                // Update hidden numeric input
                if (this.numericInput) {
                    this.numericInput.value = total;
                    this.numericInput.dispatchEvent(new Event("input", { bubbles: true }));
                    this.numericInput.dispatchEvent(new Event("change", { bubbles: true }));
                }
            }

            // 1) Add these helpers anywhere inside the class (e.g. above _initProgressiveDisclosure)

            _getColorExcludePhrases() {
            // Optional: allow overrides from markup:
            // <bold-quantity-picker data-color-exclude="# of colors, number of colors, ink colors"></bold-quantity-picker>
            const attr = (this.getAttribute('data-color-exclude') || '')
                .split(',')
                .map(s => s.trim().toLowerCase())
                .filter(Boolean);

            // Defaults (add more here as needed)
            const defaults = [
                '# of colors for front design',
                '# of colors for back design',
                '# of colors for left sleeve design',
                '# of colors for right sleeve design',
                'ink colors (front)',
                'ink colors (back)',
                'ink colors (left sleeve)',
                'ink colors (right sleeve)'
            ];

            // Dedup
            return Array.from(new Set([...defaults, ...attr]));
            }

            _isExcludedColorText(text) {
            const t = String(text || '').toLowerCase();
            const phrases = this._getColorExcludePhrases();
            return phrases.some(p => p && t.includes(p));
            }

            _isRealColorOption(opt) {
            const name = String(opt?.name || '');
            const pretty = String(opt?.prettyName || '');
            const haystack = `${name} ${pretty}`.trim();

            // Must actually be a "color/colour" option…
            const looksLikeColor = /\bcolou?rs?\b/i.test(haystack);
            if (!looksLikeColor) return false;

            // …but NOT one of the excluded “color count / ink color” style options
            if (this._isExcludedColorText(haystack)) return false;

            return true;
            }

            _isRealColorKey(key) {
            // selections keys are labels like "Shirt Color", "Front # of Colors", etc.
            const k = String(key || '');
            if (!/\bcolou?rs?\b/i.test(k)) return false;
            if (this._isExcludedColorText(k)) return false;
            return true;
            }

            _initProgressiveDisclosure() {
                if (!this.facade || !this.facade.options) {
                    this.isUnlocked = true;
                    this._togglePlaceholder(false);
                    return;
                }

                const options = this.facade.options.getOptionSets();
                this.colorOption = options.find((o) => this._isRealColorOption(o));

                if (!this.colorOption) {
                    this.isUnlocked = true;
                    this._togglePlaceholder(false);
                    return;
                }

                this.isUnlocked = false;
                this._togglePlaceholder(true);

                document.addEventListener("change", this._checkUnlock);
                document.addEventListener("input", this._checkUnlock);
                document.addEventListener(
                    this.EVENTS.STATE_CHANGE_GENERAL,
                    this._checkUnlock
                );

                // Check immediately in case defaults are already applied.
                this._checkUnlock();

                // Check again after DOM/state finishes settling.
                requestAnimationFrame(() => {
                    this._checkUnlock();
                });

                setTimeout(() => {
                    this._checkUnlock();
                }, 100);
            }
            
            _checkUnlock() {
                if (!this.facade || !this.facade.state) return;
                if (!this.colorOption) return;

                const selections =
                    this.facade.state.getSelection?.() || {};

                const foundSelection = Object.entries(selections).find(
                    ([key, value]) => {
                        if (!this._isRealColorKey(key)) return false;

                        if (value === null || value === undefined) {
                            return false;
                        }

                        if (
                            typeof value === "string" &&
                            value.trim() === ""
                        ) {
                            return false;
                        }

                        return true;
                    }
                );

                let domHasSelection = false;

                const optionName =
                    this.colorOption.prettyName ||
                    this.colorOption.name;

                if (optionName) {
                    const safeName = CSS.escape(
                        `properties[${optionName}]`
                    );

                    const input = document.querySelector(
                        `[name="${safeName}"]`
                    );

                    if (input) {
                        if (input.tagName === "SELECT") {
                            domHasSelection =
                                !!input.value &&
                                !input.value.startsWith("--");
                        } else if (
                            input.type === "radio" ||
                            input.type === "checkbox"
                        ) {
                            const checked = document.querySelector(
                                `[name="${safeName}"]:checked`
                            );

                            domHasSelection = !!checked;
                        } else {
                            domHasSelection =
                                String(input.value || "").trim() !== "";
                        }
                    }
                }

                const shouldUnlock =
                    !!foundSelection || domHasSelection;

                if (!shouldUnlock) return;

                const wasUnlocked = this.isUnlocked;

                this.isUnlocked = true;

                // Always reconcile the UI even if state already thought it was unlocked.
                this._togglePlaceholder(false);

                if (
                    !wasUnlocked &&
                    this.facade.tools &&
                    typeof this.facade.tools.dispatch === "function"
                ) {
                    this.facade.tools.dispatch("UI_UNLOCK", {
                        field: foundSelection
                            ? foundSelection[0]
                            : optionName,
                        reason: "selection-confirmed",
                    });
                }
            }

            _togglePlaceholder(show) {
                const container = this.querySelector(".quantity-slider-group");
                if (!container) return;
                const inputs =
                    container.querySelector(".matrix-grid-container") || container.querySelector(".qty-controls-row");
                let existingPlaceholder = container.querySelector(".bold-qty-placeholder");
                if (show) {
                    if (inputs) inputs.style.display = "none";
                    if (!existingPlaceholder) {
                        existingPlaceholder = document.createElement("div");
                        existingPlaceholder.className = "bold-qty-placeholder bold-load-animation";
                        const label = this.colorOption ? this.colorOption.prettyName || "Color" : "Color";
                        existingPlaceholder.innerHTML = `<span>Select a <strong>${label}</strong> above to add sizes</span>`;
                        if (inputs) container.insertBefore(existingPlaceholder, inputs);
                        else container.prepend(existingPlaceholder);
                    }
                } else {
                    if (inputs) inputs.style.display = inputs.classList.contains("qty-controls-row") ? "flex" : "grid";
                    if (existingPlaceholder) existingPlaceholder.remove();
                }
            }

            _mergeReceipt() {
                const receipt = document.querySelector("bold-price-display");
                const mainContainer = this.closest(".bold-quantity-block");
                if (receipt && mainContainer) {
                    mainContainer.appendChild(receipt);
                    const baseRow = receipt.querySelector('.bold-row:has([data-slot="base"])');
                    if (baseRow) baseRow.style.setProperty("display", "none", "important");
                    const unitRow = receipt.querySelector('[data-row="unit-total"]');
                    if (unitRow) unitRow.style.setProperty("display", "none", "important");
                    const greyBox = mainContainer.querySelector(".quantity-slider-group");
                    if (greyBox) greyBox.classList.add("has-integrated-footer");
                }
            }

            _getMatrixTarget() {
                if (!this.facade) return null;

                const allOptions = this.facade.options.getOptionSets();

                const sizeCandidates = allOptions.filter((opt) => {
                    const name = String(opt.name || '')
                        .replace(/\*/g, '')
                        .trim()
                        .toLowerCase();

                    return /^(size|sizes)(?:\s*#?2)?$/.test(name);
                });

                const hasPrimarySize = sizeCandidates.some((opt) => {
                    const name = String(opt.name || '')
                        .replace(/\*/g, '')
                        .trim()
                        .toLowerCase();

                    return name === 'size' || name === 'sizes';
                });

                const hasSecondSize = sizeCandidates.some((opt) => {
                    const name = String(opt.name || '')
                        .replace(/\*/g, '')
                        .trim()
                        .toLowerCase();

                    return /^(size|sizes)\s*#?2$/.test(name);
                });

                // Package product: Size + Size #2
                // Do not use the single-product matrix grid.
                if (hasPrimarySize && hasSecondSize) {
                    return null;
                }

                const target = sizeCandidates.find((opt) => {
                    const name = String(opt.name || '')
                        .replace(/\*/g, '')
                        .trim()
                        .toLowerCase();

                    return name === 'size' || name === 'sizes';
                });

                if (!target) return null;

                if (target.values.length > 20) return null;

                return target;
            }

            _swapPosition(targetName) {
                const safeName = targetName.replace(/"/g, '\\"');
                let originalInput = document.querySelector(`[name="properties[${safeName}]"]`);
                if (!originalInput)
                    originalInput = document.querySelector(`[name="properties[Size]"], [name="properties[size]"]`);
                if (originalInput) {
                    const container = originalInput.closest(".bold_option");
                    if (container && container.parentNode) {
                        container.classList.add("bold_option_hidden");
                        container.style.display = "none";
                        // container.parentNode.insertBefore(this, container.nextSibling);
                        // Moves to end of first container to ensure it is the last option in Step 1
                        container.parentNode.append(this);
                    }
                    originalInput.removeAttribute("required");
                }
                const form = this.closest("form");
                /*
                const submitWrapper = form ? form.querySelector(".product-form__buttons") : null;
                if (submitWrapper) {
                    submitWrapper.style.display = "none";
                }
                */
            }

            _renderMatrixMode(optionData) {
                document.body.classList.add("matrix-mode-active");
                const receipt = document.querySelector("bold-price-display");
                if (receipt) receipt.classList.add("bold-mode-matrix");
                if (this.wrapper) this.wrapper.style.display = "none";
                const headerLabel = `${optionData.prettyName} & Quantity`;
                this.innerHTML = `<div class="bold_option bold_option_displaytext"><span class="bold_option_title">${headerLabel}</span><span class="bold_option_element"><p>${headerLabel}</p></span></div><div class="quantity-slider-group matrix-wrapper"><div class="matrix-grid-container" style="background: transparent; padding: 0; margin-bottom: 0;">${optionData.valuesData.map((val) => this._renderMatrixCard(val)).join("")}</div></div>`;
                this.optionName = optionData.prettyName;
                this.querySelectorAll(".matrix-qty-input").forEach((input) => {
                    input.addEventListener("input", this._handleMatrixInput);
                    input.addEventListener("change", this._handleMatrixInput);
                });
                optionData.valuesData.forEach((val) => {
                    this.facade.actions.updateMatrixQuantitySilent(this.optionName, val.value, 0);
                });
                this.facade.actions.recalculateAll();
                // this._checkUnlock();
                // this._moveTable();
            }

            _renderMatrixCard(val) {
                const priceTxt = val.price > 0 ? `+ $${(val.price / 100).toFixed(2)}` : "";
                const labelText = this.facade.helpers.normalizeSizeLabel ? this.facade.helpers.normalizeSizeLabel(val.value) : val.value;
                return `<div class="matrix-card"><div class="matrix-info"><span class="matrix-label">${labelText}</span><span class="matrix-price">${priceTxt}</span></div><div class="matrix-input-wrapper"><div class="matrix-quantity"><button class="matrix-stepper-btn" type="button" onclick="this.nextElementSibling.stepDown(); this.nextElementSibling.dispatchEvent(new Event('input', {bubbles:true}))"><svg width="10" height="2" viewBox="0 0 10 2" fill="currentColor"><path d="M0 0h10v2H0z"/></svg></button><input class="matrix-qty-input" type="number" min="0" data-value-name="${val.value}" value="0" placeholder="0" autocomplete="off"><button class="matrix-stepper-btn" type="button" onclick="this.previousElementSibling.stepUp(); this.previousElementSibling.dispatchEvent(new Event('input', {bubbles:true}))"><svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor"><path d="M4 4V0h2v4h4v2H6v4H4V6H0V4h4z"/></svg></button></div></div></div>`;
            }

            _handleMatrixInput(e) {
                const input = e.target;
                const valueName = input.dataset.valueName;
                const qty = parseInt(input.value, 10) || 0;
                this.facade.actions.updateMatrixQuantity(this.optionName, valueName, qty);
                const card = input.closest(".matrix-card");
                if (qty > 0) card.classList.add("is-active");
                else card.classList.remove("is-active");
                if (this.originalMatrixInput) {
                    const selectedSize =
                        Array.from(this.querySelectorAll(".matrix-card.is-active")).map(
                            (c) => c.querySelector(".matrix-qty-input").dataset.valueName
                        )[0] || "";
                    if (selectedSize) {
                        this.originalMatrixInput.value = selectedSize;
                        this.originalMatrixInput.dispatchEvent(new Event("change", { bubbles: true }));
                    }
                }
            }

            _renderSliderMode() {
                this._initSliderMath();
                const min = 1;
                const max = this.sliderMax;
                const currentReal = parseInt(this.numericInput.value) || 1;
                const currentSlider = this._getSliderValue(currentReal);
                const displayMax = this.SLIDER_ZONES[this.SLIDER_ZONES.length - 1].max;
                this.innerHTML = `<div class="qty-slider-container"><input type="range" class="qty-range-input" min="${min}" max="${max}" value="${currentSlider}" step="1"><div class="qty-slider-labels"><span class="qty-minmax">${min}</span><span class="qty-minmax">${displayMax}+</span></div></div>`;
                this.rangeInput = this.querySelector(".qty-range-input");
                this.rangeInput.addEventListener("input", this._handleSliderChange);
                this.numericInput.addEventListener("input", this._handleNumericInput);
                this.numericInput.addEventListener("change", this._handleNumericInput);
                if (this.wrapper && this.wrapper.parentNode) {
                    const parent = this.wrapper.parentNode;
                    const mainContainer = document.createElement("div");
                    mainContainer.className = "bold-quantity-block";
                    mainContainer.style.width = "100%";
                    mainContainer.style.marginBottom = "20px";
                    const sectionHeader = document.createElement("div");
                    sectionHeader.className = "bold_option bold_option_displaytext";
                    sectionHeader.innerHTML = `<span class="bold_option_title">Quantity</span><span class="bold_option_element"><p>Quantity</p></span>`;
                    const flexGroup = document.createElement("div");
                    flexGroup.className = "quantity-slider-group";
                    const controlsRow = document.createElement("div");
                    controlsRow.className = "qty-controls-row";
                    parent.insertBefore(mainContainer, this.wrapper);
                    mainContainer.appendChild(sectionHeader);
                    mainContainer.appendChild(flexGroup);
                    flexGroup.appendChild(controlsRow);
                    controlsRow.appendChild(this);
                    controlsRow.appendChild(this.wrapper);
                }
            }
            _initSliderMath() {
                let realCursor = 0;
                let sliderCursor = 0;
                this.calculatedZones = this.SLIDER_ZONES.map((zone) => {
                    const realRange = zone.max - realCursor;
                    const sliderTicks = Math.ceil(realRange / zone.step);
                    const data = {
                        realMin: realCursor,
                        realMax: zone.max,
                        step: zone.step,
                        sliderMin: sliderCursor,
                        sliderMax: sliderCursor + sliderTicks,
                    };
                    realCursor = zone.max;
                    sliderCursor += sliderTicks;
                    return data;
                });
                this.sliderMax = sliderCursor;
                console.log('facade state', this.facade.state);
            }
            _getRealValue(sliderVal) {
                const v = parseInt(sliderVal, 10);
                const zone =
                    this.calculatedZones.find((z) => v <= z.sliderMax) ||
                    this.calculatedZones[this.calculatedZones.length - 1];
                const val = zone.realMin + (v - zone.sliderMin) * zone.step;
                return Math.min(val, zone.realMax);
            }
            _getSliderValue(realQty) {
                const qty = parseInt(realQty, 10);
                const zone =
                    this.calculatedZones.find((z) => qty <= z.realMax) ||
                    this.calculatedZones[this.calculatedZones.length - 1];
                return zone.sliderMin + Math.round((qty - zone.realMin) / zone.step);
            }
            _handleSliderChange(e) {
                const realQty = this._getRealValue(e.target.value);
                // Update numeric display immediately so user sees value change
                this.numericInput.value = realQty;
                // Update numeric input display in the numericInput
                this._showSliderLoading(true);
                // Debounce the facade recalc — only fire when user stops dragging
                clearTimeout(this._sliderDebounce);
                this._sliderDebounce = setTimeout(() => {
                    this.numericInput.dispatchEvent(new Event("change", { bubbles: true }));
                    this._showSliderLoading(false);
                }, 300);
            }
            _showSliderLoading(show) {
                // Dim the two qty counter elements during debounce
                const targets = [
                    document.getElementById('header-qty-counter'),
                    document.querySelector('[data-slot="qty"]'),
                    document.querySelector('[data-slot="grand-total"]'),
                ];
                targets.forEach(el => {
                    if (el) el.style.opacity = show ? '0.35' : '';
                });
            }
            _handleNumericInput(e) {
                if (this.multiplierMode || this.countMode) return; // numeric input is read-only/computed in these modes
                const sliderVal = this._getSliderValue(e.target.value);
                if (this.rangeInput) this.rangeInput.value = sliderVal;
            }

            // ────────────────────────────────────────────────────────────
            // MULTIPLIER MODE
            // Used by customize (Greek letter) products: the slider/steppers
            // control an integer "sets" value, and final quantity is
            // sets * getMultiplicand() (typically total letter count).
            // ────────────────────────────────────────────────────────────

            /**
             * Switches the picker from normal quantity-slider behavior into
             * "sets" mode.
             * @param {Object} config
             * @param {number} config.min - minimum sets (usually 1)
             * @param {number} config.max - maximum sets (e.g. 20 for twill, 25 for wood)
             * @param {number} config.step - slider/stepper increment (usually 1)
             * @param {number} [config.initialValue=1] - starting sets value
             * @param {Function} config.getMultiplicand - () => number, e.g. total letter count
             */
            enableMultiplierMode(config = {}) {
                const { min = 1, max = 50, step = 1, initialValue = 1, getMultiplicand } = config;

                if (typeof getMultiplicand !== 'function') {
                    console.warn('[BoldQuantityPicker] enableMultiplierMode requires a getMultiplicand() function.');
                    return;
                }

                if (!this.numericInput || !this.wrapper) {
                    console.warn('[BoldQuantityPicker] enableMultiplierMode called before init(); ignoring.');
                    return;
                }

                this.multiplierMode = { min, max, step, getMultiplicand };
                this.sets = Math.min(Math.max(parseInt(initialValue, 10) || min, min), max);

                this._renderMultiplierSlider();
                this._lockNumericInput(true);
                this._bindStepperCapture();
                this._applyMultiplier();
            }

            /**
             * Reverts the picker to normal zone-based quantity slider behavior.
             * Safe to call even if multiplier mode was never enabled.
             */
            disableMultiplierMode() {
                if (!this.multiplierMode) return;

                this.multiplierMode = null;
                this._unbindStepperCapture();
                this._removeSetsHint();
                this._removeSetsHiddenInput(); 
                this._lockNumericInput(false);

                // Revert to the standard zone-based slider.
                this._renderSliderMode();
            }

            /**
             * Re-reads getMultiplicand() and re-applies quantity using the
             * current sets value. Call this whenever the multiplicand changes
             * (e.g. the user edits letter text) without the user having
             * touched the slider/steppers.
             */
            recomputeMultiplier() {
                if (!this.multiplierMode) return;
                this._applyMultiplier();
            }

            /**
             * Public setter for sets. Clamps to the configured [min, max].
             * No-ops if multiplier mode isn't active.
             */
            setSets(n) {
                this._setSets(n);
            }

            _renderMultiplierSlider() {
                const { min, max, step } = this.multiplierMode;
                this.innerHTML = `<div class="qty-slider-container qty-slider-container--multiplier">` +
                    `<input type="range" class="qty-range-input" min="${min}" max="${max}" step="${step}" value="${this.sets}">` +
                    `<div class="qty-slider-labels"><span class="qty-minmax">${min}</span><span class="qty-minmax">${max}</span></div>` +
                    `</div>`;
                this.rangeInput = this.querySelector('.qty-range-input');
                this.rangeInput.addEventListener('input', this._handleMultiplierSliderChange);

                this._ensureSetsHint();
            }

            _handleMultiplierSliderChange(e) {
                const val = parseInt(e.target.value, 10);
                this._setSets(Number.isNaN(val) ? this.multiplierMode.min : val);
            }

            /**
             * Intercepts clicks on the native quantity-input's -/+ buttons
             * while multiplier mode is active, repurposing them to adjust
             * "sets" instead of raw quantity. Bound on the wrapper's capture
             * phase so it runs before the native component's own click
             * handling on the button itself (which fires during the target
             * phase, after any ancestor capture-phase listener).
             */
            _bindStepperCapture() {
                if (!this.wrapper || this._stepperCaptureBound) return;
                this.wrapper.addEventListener('click', this._handleStepperClickCapture, { capture: true });
                this._stepperCaptureBound = true;
            }

            _unbindStepperCapture() {
                if (!this.wrapper || !this._stepperCaptureBound) return;
                this.wrapper.removeEventListener('click', this._handleStepperClickCapture, { capture: true });
                this._stepperCaptureBound = false;
            }

            _handleStepperClickCapture(e) {
                if (!this.multiplierMode) return;

                const btn = e.target.closest('button[name="minus"], button[name="plus"]');
                if (!btn || !this.wrapper.contains(btn)) return;

                e.preventDefault();
                e.stopPropagation();

                const { step, getMultiplicand } = this.multiplierMode;
                const letters = Math.max(0, parseInt(getMultiplicand(), 10) || 0);

                if (btn.name === 'plus') {
                    // Mirror the legacy guard: don't allow increasing sets
                    // until at least one letter has been entered.
                    if (letters <= 0) return;
                    this._setSets(this.sets + step);
                } else if (btn.name === 'minus') {
                    this._setSets(this.sets - step);
                }
            }

            _setSets(n) {
                if (!this.multiplierMode) return;
                const { min, max } = this.multiplierMode;
                const val = parseInt(n, 10);
                const clamped = Math.min(Math.max(Number.isNaN(val) ? min : val, min), max);

                this.sets = clamped;
                if (this.rangeInput) this.rangeInput.value = clamped;

                this._applyMultiplier();
            }

            // ────────────────────────────────────────────────────────────
            // COUNT MODE
            // Quantity equals an externally supplied count (e.g. number of
            // names in a group order). No sets. The slider and +/- buttons
            // are hidden via CSS (.is-count-mode), not torn down, so leaving
            // the mode needs no re-render.
            // ────────────────────────────────────────────────────────────

            /**
             * @param {Object} config
             * @param {Function} config.getCount - () => number
             * @param {Function} [config.formatHint] - (count) => string
             */
            enableCountMode(config = {}) {
                const { getCount, formatHint } = config;

                if (typeof getCount !== 'function') {
                    console.warn('[BoldQuantityPicker] enableCountMode requires a getCount() function.');
                    return;
                }
                if (!this.numericInput || !this.wrapper) {
                    console.warn('[BoldQuantityPicker] enableCountMode called before init(); ignoring.');
                    return;
                }

                // Already on: just refresh the callbacks and recompute.
                if (this.countMode) {
                    this.countMode = { getCount, formatHint };
                    this._applyCount();
                    return;
                }

                if (this.multiplierMode) this.disableMultiplierMode();

                this._savedQuantity = parseInt(this.numericInput.value, 10) || 1;
                this._savedMin = this.numericInput.getAttribute('min');
                this._lastCountApplied = null;
                this.countMode = { getCount, formatHint };

                this.classList.add('is-count-mode');
                this.wrapper.classList.add('is-count-mode');
                this.numericInput.setAttribute('min', '0'); // allow 0 until names exist
                this._lockNumericInput(true);
                this._applyCount();
            }

            /** Leaves count mode and restores the quantity the customer had before. */
            disableCountMode() {
                if (!this.countMode) return;

                clearTimeout(this._countDebounce);
                this.countMode = null;
                this._lastCountApplied = null;

                this._removeCountHint();
                this._lockNumericInput(false);
                this.classList.remove('is-count-mode');
                if (this.wrapper) this.wrapper.classList.remove('is-count-mode');
                this._showSliderLoading(false);

                if (this._savedMin === null) this.numericInput.removeAttribute('min');
                else this.numericInput.setAttribute('min', this._savedMin);

                // countMode is null now, so _handleNumericInput re-syncs the slider.
                this.numericInput.value = Math.max(1, this._savedQuantity || 1);
                this.numericInput.dispatchEvent(new Event('input', { bubbles: true }));
                this.numericInput.dispatchEvent(new Event('change', { bubbles: true }));
            }

            /** Call when the count changes externally (e.g. on textarea input). */
            recomputeCount() {
                if (!this.countMode) return;
                this._applyCount();
            }

            _applyCount() {
                if (!this.countMode || !this.numericInput) return;

                const count = Math.max(0, parseInt(this.countMode.getCount(), 10) || 0);
                this._updateCountHint(count);

                // Skip redundant dispatches (sync can run several times per keystroke).
                if (this._lastCountApplied === count && String(this.numericInput.value) === String(count)) return;
                this._lastCountApplied = count;

                this.numericInput.value = count;
                this.numericInput.dispatchEvent(new Event('input', { bubbles: true }));

                this._showSliderLoading(true);
                clearTimeout(this._countDebounce);
                this._countDebounce = setTimeout(() => {
                    this.numericInput.dispatchEvent(new Event('change', { bubbles: true }));
                    this._showSliderLoading(false);
                }, 150);
            }

            _ensureCountHint() {
                if (this._countHintEl && this._countHintEl.isConnected) return this._countHintEl;

                const hint = document.createElement('p');
                hint.className = 'letter-sets-hint count-mode-hint'; // reuses the existing hint styling

                if (this.wrapper && this.wrapper.parentNode) {
                    this.wrapper.parentNode.insertBefore(hint, this.wrapper.nextSibling);
                } else if (this.numericInput && this.numericInput.parentNode) {
                    this.numericInput.parentNode.appendChild(hint);
                }

                this._countHintEl = hint;
                return hint;
            }

            _updateCountHint(count) {
                const hint = this._ensureCountHint();
                const fmt = this.countMode && this.countMode.formatHint;
                hint.textContent = typeof fmt === 'function'
                    ? fmt(count)
                    : (count === 0 ? 'Enter details above to calculate quantity' : `${count} ${count === 1 ? 'item' : 'items'}`);
                hint.classList.toggle('letter-sets-hint--empty', count === 0);
            }

            _removeCountHint() {
                if (this._countHintEl) {
                    this._countHintEl.remove();
                    this._countHintEl = null;
                }
            }

            _applyMultiplier() {
                if (!this.multiplierMode || !this.numericInput) return;

                const letters = Math.max(0, parseInt(this.multiplierMode.getMultiplicand(), 10) || 0);
                const finalQty = letters * this.sets;

                this._updateSetsHintText(letters);
                this._updateSetsHiddenInput();

                this.numericInput.value = finalQty;
                this.numericInput.dispatchEvent(new Event('input', { bubbles: true }));

                this._showSliderLoading(true);
                clearTimeout(this._multiplierDebounce);
                this._multiplierDebounce = setTimeout(() => {
                    this.numericInput.dispatchEvent(new Event('change', { bubbles: true }));
                    this._showSliderLoading(false);
                }, 150);
            }

            _lockNumericInput(lock) {
                if (!this.numericInput) return;
                this.numericInput.readOnly = lock;
                this.numericInput.classList.toggle('is-computed', lock);
            }

            _ensureSetsHint() {
                if (this._setsHintEl && this._setsHintEl.isConnected) return this._setsHintEl;

                const hint = document.createElement('p');
                hint.className = 'letter-sets-hint';

                if (this.wrapper && this.wrapper.parentNode) {
                    this.wrapper.parentNode.insertBefore(hint, this.wrapper.nextSibling);
                } else if (this.numericInput && this.numericInput.parentNode) {
                    this.numericInput.parentNode.appendChild(hint);
                }

                this._setsHintEl = hint;
                return hint;
            }

            _updateSetsHintText(letters) {
                const hint = this._ensureSetsHint();

                if (letters <= 0) {
                    hint.textContent = 'Enter letters above to calculate quantity';
                    hint.classList.add('letter-sets-hint--empty');
                    return;
                }

                hint.classList.remove('letter-sets-hint--empty');
                const setLabel = this.sets === 1 ? 'set' : 'sets';
                hint.textContent = `${this.sets} ${setLabel} of letters`;
            }

            _removeSetsHint() {
                if (this._setsHintEl) {
                    this._setsHintEl.remove();
                    this._setsHintEl = null;
                }
            }

            _ensureSetsHiddenInput() {
                if (this._setsHiddenEl && this._setsHiddenEl.isConnected) return this._setsHiddenEl;

                const hidden = document.createElement('input');
                hidden.type = 'hidden';
                hidden.name = 'properties[Total # of letter sets]';

                const formAttr = this.numericInput && this.numericInput.getAttribute('form');
                if (formAttr) hidden.setAttribute('form', formAttr);

                if (this.wrapper && this.wrapper.parentNode) {
                    this.wrapper.parentNode.insertBefore(hidden, this.wrapper.nextSibling);
                } else if (this.numericInput && this.numericInput.parentNode) {
                    this.numericInput.parentNode.appendChild(hidden);
                }

                this._setsHiddenEl = hidden;
                return hidden;
            }

            _updateSetsHiddenInput() {
                const hidden = this._ensureSetsHiddenInput();
                hidden.value = `${this.sets} ${this.sets === 1 ? 'set' : 'sets'} of letters`;
            }

            _removeSetsHiddenInput() {
                if (this._setsHiddenEl) {
                    this._setsHiddenEl.remove();
                    this._setsHiddenEl = null;
                }
            }
        }
    );
}
